import styles from './Segmented.module.css';

export interface SegmentOption<T extends string | number> {
  value: T;
  label: string;
  /** Accessible name when the label alone is unclear (e.g. "♯"). */
  ariaLabel?: string;
}

interface SegmentedProps<T extends string | number> {
  label: string;
  options: SegmentOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  /** Equal-width segments filling the container. */
  stretch?: boolean;
  tone?: 'surface' | 'subtle';
}

/** A row of toggle buttons where one is pressed (design: pill segmented controls). */
export function Segmented<T extends string | number>({
  label,
  options,
  value,
  onChange,
  stretch = false,
  tone = 'subtle',
}: SegmentedProps<T>) {
  return (
    <div
      role="group"
      aria-label={label}
      className={styles.group}
      data-stretch={stretch || undefined}
      data-tone={tone}
      style={
        stretch ? { gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` } : undefined
      }
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={styles.segment}
          aria-pressed={o.value === value}
          aria-label={o.ariaLabel}
          onClick={() => {
            onChange(o.value);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
